import { FiActivity } from 'react-icons/fi';

export const presentContinuousData = {
  courseId: 'present-continuous',
  courseType: 'grammar',
  title: 'Present Continuous Tense',
  icon: FiActivity,
  overview: {
    impact: 'Describing current and temporary actions',
    focus: 'Actions happening now or around now',
    uses: 'Speaking descriptions, Writing Task 1 trends',
    duration: '20-25 minutes'
  },
  sections: [
    {
      title: 'Formation',
      content: `<p>Subject + <strong>am/is/are</strong> + verb<strong>-ing</strong></p><p>I <strong>am working</strong>. She <strong>is reading</strong>. They <strong>are playing</strong>.</p>`,
      examples: [
        {
          label: 'Now',
          text: 'I am studying for IELTS right now.'
        },
        {
          label: 'Temporary',
          text: 'She is living in London this year.'
        },
        {
          label: 'Trend',
          text: 'Prices are rising steadily.'
        }
      ]
    },
    {
      title: 'Spelling Rules',
      content: `<p>Most verbs: add <strong>-ing</strong> (play→playing). Verbs ending in -e: drop e, add -ing (make→making). Short verbs (CVC): double final consonant (run→running, sit→sitting).</p>`,
      table: {
        headers: [
          'Base',
          '+ ing',
          'Rule'
        ],
        rows: [
          [
            'play',
            'playing',
            'add -ing'
          ],
          [
            'make',
            'making',
            'drop -e'
          ],
          [
            'run',
            'running',
            'double consonant'
          ],
          [
            'lie',
            'lying',
            'ie→y'
          ],
          [
            'begin',
            'beginning',
            'double consonant'
          ]
        ]
      }
    },
    {
      title: 'Present Simple vs Present Continuous',
      content: `<p>Understanding the difference is crucial for IELTS:</p>`,
      points: [
        'Simple = permanent/habitual: \'I live in Paris.\'',
        'Continuous = temporary/now: \'I am living in Paris this month.\'',
        'Stative verbs don\'t use continuous: know, believe, want, need',
        'Continuous for changing trends in Task 1: \'The number is increasing.\''
      ]
    }
  ],
  exercises: [
    {
      title: 'Simple or Continuous?',
      type: 'select',
      instructions: 'Choose the correct tense.',
      questions: [
        {
          text: 'Look! It ___ (rain).',
          options: [
            'rains',
            'is raining',
            'rain'
          ],
          answer: 'is raining',
          explanation: 'Happening right now → present continuous.'
        },
        {
          text: 'She ___ (work) in a bank. It\'s her permanent job.',
          options: [
            'works',
            'is working',
            'working'
          ],
          answer: 'works',
          explanation: 'Permanent situation → present simple.'
        },
        {
          text: 'I ___ (read) a great book this week.',
          options: [
            'read',
            'am reading',
            'reads'
          ],
          answer: 'am reading',
          explanation: 'Temporary action around now → present continuous.'
        },
        {
          text: 'The Earth ___ (revolve) around the Sun.',
          options: [
            'revolves',
            'is revolving',
            'revolve'
          ],
          answer: 'revolves',
          explanation: 'Scientific fact → present simple.'
        }
      ]
    },
    {
      title: 'Write the -ing Form',
      type: 'writing',
      instructions: 'Write the correct -ing form of each verb.',
      questions: [
        {
          text: 'swim',
          example: 'swimming',
          tip: 'Short CVC verb → double the final consonant.'
        },
        {
          text: 'write',
          example: 'writing',
          tip: 'Drop the -e before adding -ing.'
        },
        {
          text: 'die',
          example: 'dying',
          tip: 'Change -ie to -y before adding -ing.'
        }
      ]
    }
  ],
  tips: [
    'Use for actions happening NOW or AROUND NOW',
    'Key signal words: now, at the moment, currently, this week',
    'Don\'t use with stative verbs (know, want, believe, need)',
    'Great for describing trends in IELTS Writing Task 1'
  ],
  dailyPractice: [
    'Practice present continuous tense concepts daily',
    'Review and apply in IELTS practice tests',
    'Write example sentences using key concepts',
    'Teach the concept to someone else to reinforce understanding'
  ],
  backPath: '/exams/english/#grammar',
  backLabel: 'Back to Grammar',
  aiContext: 'Present Continuous: am/is/are + -ing for current actions, temporary situations, and changing trends. Contrast with present simple for IELTS accuracy.'
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...presentContinuousData,
      title: presentContinuousData.title + ' (Foundations)',
      overview: { ...presentContinuousData.overview, duration: '15-20 minutes' },
      sections: presentContinuousData.sections.slice(0, Math.max(2, Math.ceil(presentContinuousData.sections.length / 2))),
      exercises: presentContinuousData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...presentContinuousData,
      title: presentContinuousData.title + ' (Advanced)',
      overview: { ...presentContinuousData.overview, duration: '30-35 minutes' },
      sections: presentContinuousData.sections,
      exercises: presentContinuousData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(presentContinuousData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS tasks.',
      ],
    };
  }
  return presentContinuousData;
}
